import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { CheckCircle, AlertTriangle, FileText } from 'lucide-react';

export const AcceptableUsePage: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const menuItems = [
        { label: 'Features', href: '/#features' },
        { label: 'Blog', href: '/blog' },
        { label: 'Press', href: '/press' },
        { label: 'Pitch', href: '/pitch' },
        { label: 'Enterprise API', href: '/enterprise-api' }
    ];

    return (
        <div className="min-h-screen bg-white">
            <Helmet>
                <title>Acceptable Use Policy – Cura Service Guidelines</title>
                <meta name="description" content="Review Cura's acceptable use policy for responsible usage of our AI medical assistant, including prohibited activities and medical disclaimers ✓" />
                <link rel="canonical" href="https://mycura.org/acceptable-use" />
                <meta property="og:title" content="Acceptable Use Policy – Cura Service Guidelines" />
                <meta property="og:description" content="Review Cura's acceptable use policy for responsible usage of our AI medical assistant, including prohibited activities and medical disclaimers." />
                <meta property="og:url" content="https://mycura.org/acceptable-use" />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Acceptable Use Policy – Cura Service Guidelines" />
                <meta name="twitter:description" content="Review Cura's acceptable use policy for responsible usage of our AI medical assistant, including prohibited activities and medical disclaimers." />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "name": "Cura Acceptable Use Policy",
                        "url": "https://mycura.org/acceptable-use",
                        "isPartOf": { "@type": "WebSite", "name": "Cura", "url": "https://mycura.org" }
                    })}
                </script>
            </Helmet>
            <Navigation
                menuItems={menuItems}
                scrollToSection={(id) => {
                    const element = document.getElementById(id);
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                scrollToTop={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            />

            <main className="pt-32 pb-24">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center mb-16"
                    >
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-50 text-[#003b82] mb-6">
                            <FileText className="w-8 h-8" />
                        </div>
                        <h1 className="text-4xl md:text-5xl text-slate-900 mb-6">
                            Acceptable Use Policy
                        </h1>
                        <p className="text-xl text-slate-500 max-w-2xl mx-auto">
                            Guidelines for using Cura responsibly and safely.
                        </p>
                    </motion.div>

                    <div className="prose prose-lg prose-slate max-w-none">
                        <p className="lead">
                            This Acceptable Use Policy ("AUP") outlines the examples of prohibited use of the Cura services. This AUP is an integral part of your agreement with Cura.
                        </p>

                        <h3>1. No Illegal, Harmful, or Offensive Use or Content</h3>
                        <p>
                            You may not use, or encourage, promote, facilitate or instruct others to use, the Services for any illegal, harmful, fraudulent, infringing or offensive use, or to transmit, store, display, distribute or otherwise make available content that is illegal, harmful, fraudulent, infringing or offensive.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
                            <div className="bg-red-50 p-6 rounded-xl border border-red-100">
                                <h4 className="flex items-center gap-2 text-red-700 mt-0 mb-3">
                                    <AlertTriangle className="w-5 h-5" />
                                    Prohibited Activities
                                </h4>
                                <ul className="text-sm text-red-800 mb-0 pl-4">
                                    <li>Illegal activities or promoting illegal acts</li>
                                    <li>Infringing intellectual property rights</li>
                                    <li>Harassment, bullying, or threatening behavior</li>
                                    <li>Distributing malware or viruses</li>
                                </ul>
                            </div>
                            <div className="bg-green-50 p-6 rounded-xl border border-green-100">
                                <h4 className="flex items-center gap-2 text-green-700 mt-0 mb-3">
                                    <CheckCircle className="w-5 h-5" />
                                    Encouraged Use
                                </h4>
                                <ul className="text-sm text-green-800 mb-0 pl-4">
                                    <li>Personal health monitoring</li>
                                    <li>Educational purposes</li>
                                    <li>Caregiver coordination</li>
                                    <li>Professional medical reference</li>
                                </ul>
                            </div>
                        </div>

                        <h3>2. Security Violations</h3>
                        <p>
                            You may not use the Services to violate the security or integrity of any network, computer or communications system, software application, or network or computing device.
                        </p>

                        <h3>3. Network Abuse</h3>
                        <p>
                            You may not make network connections to any users, hosts, or networks unless you have permission to communicate with them. Prohibited activities include:
                        </p>
                        <ul>
                            <li>Monitoring or crawling of a System that impairs or disrupts the System being monitored or crawled.</li>
                            <li>Inundating a target with communications requests so the target either cannot respond to legitimate traffic or responds so slowly that it becomes ineffective.</li>
                            <li>Interfering with the proper functioning of any System, including any deliberate attempt to overload a system by mail bombing, news bombing, broadcast attacks, or flooding techniques.</li>
                        </ul>

                        <h3>4. Medical Disclaimer</h3>
                        <p>
                            Cura is an AI-powered information tool, not a replacement for professional medical advice, diagnosis, or treatment. You may not use the service to:
                        </p>
                        <ul>
                            <li>Prescribe medication to others without a license.</li>
                            <li>Provide definitive medical diagnoses in a professional capacity without proper credentials.</li>
                            <li>Impersonate a medical professional.</li>
                        </ul>

                        <h3>5. Enforcement</h3>
                        <p>
                            We reserve the right, but do not assume the obligation, to investigate any violation of this Policy or misuse of the Services. We may:
                        </p>
                        <ul>
                            <li>Investigate violations of this Policy or misuse of the Services;</li>
                            <li>Remove, disable access to, or modify any content or resource that violates this Policy or any other agreement we have with you for use of the Services.</li>
                            <li>Report any activity that we suspect violates any law or regulation to appropriate law enforcement officials, regulators, or other appropriate third parties.</li>
                        </ul>

                        <div className="mt-12 pt-8 border-t border-slate-200">
                            <p className="text-sm text-slate-500">
                                Last updated: November 25, 2024
                            </p>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default AcceptableUsePage;
